import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, X, Loader2, Award, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { useModalBack } from "@/hooks/useModalBack";

const daysLeft = (expiry) => {
  if (!expiry) return null;
  try {
    const today = new Date(new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" }) + "T12:00:00");
    const exp = new Date(`${String(expiry).slice(0, 10)}T12:00:00`);
    return Math.round((exp - today) / 86400000);
  } catch { return null; }
};

const ExpiryBadge = ({ expiry, testid }) => {
  const d = daysLeft(expiry);
  if (d === null) return null;
  const cls = d < 0
    ? "bg-red-600 text-white"
    : d <= 7
      ? "bg-red-500/90 text-white"
      : d <= 30
        ? "bg-amber-500/90 text-black"
        : "bg-black/60 text-white/80";
  const label = d < 0 ? "expired" : d === 0 ? "today" : `${d}d`;
  return (
    <span className={`absolute bottom-0.5 right-0.5 rounded px-1 py-0.5 font-mono text-[8px] font-bold uppercase ${cls}`} data-testid={testid}>
      {label}
    </span>
  );
};

/**
 * CertificatesStrip — thumbnail row under the employee's home-page card.
 * Plus tile uploads a certificate photo (with name / description / expiry);
 * tapping a thumbnail opens the edit dialog (rename, re-date, delete).
 */
const CertificatesStrip = () => {
  const confirm = useConfirm();
  const [certs, setCerts] = useState([]);
  const [thumbs, setThumbs] = useState({}); // cert_id -> object URL
  const [dialog, setDialog] = useState(null); // { mode: "new"|"edit", file?, cert? }
  const [viewer, setViewer] = useState(null); // cert shown full-screen
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/certificates");
      setCerts(Array.isArray(data) ? data : []);
    } catch { setCerts([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch each thumbnail as an authorized blob → object URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of certs) {
        if (thumbs[c.cert_id]) continue;
        try {
          const { data } = await api.get(`/me/certificates/${c.cert_id}/image`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(data);
          setThumbs((t) => ({ ...t, [c.cert_id]: url }));
        } catch { /* missing image — show placeholder */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certs]);

  const openNew = (file) => {
    if (!file) return;
    setName((file.name || "").replace(/\.[^.]+$/, ""));
    setDesc("");
    setExpiry("");
    setDialog({ mode: "new", file });
  };
  const openEdit = (cert) => {
    setViewer(null);
    setName(cert.name || "");
    setDesc(cert.description || "");
    setExpiry(cert.expiry_date || "");
    setDialog({ mode: "edit", cert });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name the certificate"); return; }
    setBusy(true);
    try {
      if (dialog.mode === "new") {
        const fd = new FormData();
        fd.append("name", name.trim());
        fd.append("description", desc.trim());
        fd.append("expiry_date", expiry);
        fd.append("image", dialog.file);
        await api.post("/me/certificates", fd);
        toast.success("Certificate added");
      } else {
        await api.put(`/me/certificates/${dialog.cert.cert_id}`, {
          name: name.trim(), description: desc.trim(), expiry_date: expiry,
        });
        toast.success("Certificate updated");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the certificate");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this certificate?",
      description: `${dialog.cert.name} will be removed permanently.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/me/certificates/${dialog.cert.cert_id}`);
      toast.success("Certificate deleted");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally { setBusy(false); }
  };

  const editingCert = dialog?.mode === "edit" ? dialog.cert : null;
  const editUrl = editingCert ? thumbs[editingCert.cert_id] : null;
  // Memoized: creating a new object URL per render re-decodes the picked
  // photo on every keystroke — on phones that froze the dialog.
  const newUrl = useMemo(
    () => (dialog?.mode === "new" ? URL.createObjectURL(dialog.file) : null),
    [dialog],
  );
  // Hardware/gesture back closes the dialog or viewer (Android).
  useModalBack(!!dialog, () => setDialog(null));
  useModalBack(!!viewer && !dialog, () => setViewer(null));

  return (
    <div className="mt-3 border-t border-white/5 pt-2.5" data-testid="certificates-strip">
      <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">Certificates</p>
      <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5" data-testid="certificates-row">
        <label
          className="flex h-14 w-14 shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-white/25 bg-white/[0.03] text-white/50 hover:border-[#1a3fd1] hover:text-[#8fa4f0]"
          data-testid="cert-upload-plus"
          title="Upload a certificate"
        >
          <Plus className="h-4 w-4" />
          <span className="font-mono text-[7px] uppercase tracking-widest">Add</span>
          <input type="file" accept="image/*" className="hidden" data-testid="cert-upload-input"
            onChange={(e) => { openNew(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {certs.map((c) => (
          <button
            key={c.cert_id}
            type="button"
            onClick={() => setViewer(c)}
            data-testid={`cert-thumb-${c.cert_id}`}
            title={`${c.name}${c.expiry_date ? ` · expires ${c.expiry_date}` : ""}`}
            className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md border border-white/15 bg-white/5 hover:border-[#1a3fd1]"
          >
            {thumbs[c.cert_id] ? (
              <img src={thumbs[c.cert_id]} alt={c.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/30"><Award className="h-5 w-5" /></div>
            )}
            <ExpiryBadge expiry={c.expiry_date} testid={`cert-expiry-${c.cert_id}`} />
          </button>
        ))}
        {certs.length === 0 && (
          <p className="self-center font-mono text-[9px] uppercase tracking-widest text-white/35" data-testid="certificates-empty">
            No certificates yet — tap + to add one
          </p>
        )}
      </div>

      {viewer && createPortal(
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/80 p-4" onClick={() => setViewer(null)} data-testid="cert-viewer">
          <div className="max-h-full w-full max-w-lg overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white" data-testid="cert-viewer-name">{viewer.name}</p>
                <p className="truncate font-mono text-[10px] text-white/45">
                  {viewer.expiry_date ? `Expires ${viewer.expiry_date}` : "No expiry"}{viewer.description ? ` · ${viewer.description}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => openEdit(viewer)}
                  aria-label="Edit certificate"
                  title="Edit"
                  data-testid="cert-viewer-edit"
                  className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewer(null)}
                  aria-label="Close"
                  data-testid="cert-viewer-close"
                  className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex max-h-[75vh] items-center justify-center overflow-auto bg-black/40 p-3">
              {thumbs[viewer.cert_id] ? (
                <img src={thumbs[viewer.cert_id]} alt={viewer.name} className="max-h-[70vh] w-auto max-w-full rounded object-contain" data-testid="cert-viewer-img" />
              ) : (
                <Award className="h-16 w-16 text-white/25" />
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {dialog && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="cert-dialog" onClick={() => setDialog(null)}>
          <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h3 className="text-lg font-bold uppercase tracking-tight">
                {dialog.mode === "new" ? "New certificate" : "Edit certificate"}
              </h3>
              <button onClick={() => setDialog(null)} data-testid="cert-dialog-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 overflow-y-auto p-4">
              {(editUrl || newUrl) && (
                <div className="flex justify-center rounded-lg bg-black/40 p-2">
                  <img src={editUrl || newUrl} alt="" className="max-h-48 w-auto max-w-full rounded object-contain" data-testid="cert-dialog-img" />
                </div>
              )}
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Working at Heights" data-testid="cert-name"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Description</label>
                <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional notes…" data-testid="cert-desc"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Expiry date</label>
                <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="cert-expiry"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
                <p className="mt-1 font-mono text-[9.5px] text-white/40">You and your admins get reminded 30 and 7 days before it expires.</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
              {dialog.mode === "edit" ? (
                <button onClick={remove} disabled={busy} data-testid="cert-delete"
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300 disabled:opacity-60">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button onClick={() => setDialog(null)} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="cert-cancel">Cancel</button>
                <button onClick={save} disabled={busy} data-testid="cert-save"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {dialog.mode === "new" ? "Add certificate" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CertificatesStrip;
